import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { ProgressBar } from './ProgressBar';

const listVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.06, delayChildren: 0.1 } },
};
const optionVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] } },
};

export const QuestionScreen = ({ question, total, onAnswer, onBack, canBack }) => {
  return (
    <motion.section
      className="relative min-h-screen w-full bg-brand-bg flex items-center justify-center px-5 sm:px-6 pt-24 sm:pt-28 pb-12 sm:pb-16"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`question-screen-${question.id}`}
    >
      {/* Top brand bar */}
      <div className="absolute top-0 left-0 right-0 px-5 sm:px-6 md:px-10 pt-5 sm:pt-7 flex items-center justify-between max-w-7xl mx-auto">
        <img src="/brand/young-again-logo.png" alt="Young Again" className="h-8 sm:h-9 w-auto opacity-90" />
        <span className="hidden sm:block text-xs uppercase tracking-[0.22em] font-semibold text-brand-muted font-heading">
          Cellular Energy Assessment
        </span>
      </div>

      <div className="w-full max-w-2xl">
        <ProgressBar current={question.number} total={total} />

        <div className="mt-8 sm:mt-10">
          <div className="text-[11px] sm:text-xs uppercase tracking-[0.22em] font-semibold text-brand-moss font-heading" data-testid="question-overline">
            {question.overline}
          </div>
          <motion.h2
            className="mt-3 sm:mt-4 font-heading font-bold text-2xl sm:text-4xl lg:text-5xl text-brand-forest leading-[1.15] sm:leading-[1.1] tracking-tight"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            data-testid="question-text"
          >
            {question.question}
          </motion.h2>
        </div>

        <motion.div
          className="mt-8 sm:mt-10 space-y-3"
          variants={listVariants}
          initial="hidden"
          animate="visible"
          data-testid="question-options"
        >
          {question.options.map((opt, idx) => (
            <motion.button
              key={opt}
              variants={optionVariants}
              whileHover={{ x: 4 }}
              whileTap={{ scale: 0.985 }}
              onClick={() => onAnswer(opt, idx)}
              className="group w-full text-left bg-white border border-brand-border rounded-2xl px-4 sm:px-6 py-4 sm:py-5 hover:border-brand-forest hover:bg-white transition-all shadow-sm hover:shadow-md flex items-center justify-between gap-3"
              data-testid={`answer-option-${idx}`}
            >
              <span className="flex items-center gap-3 sm:gap-4 min-w-0">
                <span className="flex-shrink-0 h-8 w-8 rounded-lg border border-brand-border group-hover:border-brand-forest group-hover:bg-brand-sage flex items-center justify-center text-xs font-semibold text-brand-muted group-hover:text-brand-forest transition-colors font-heading">
                  {String.fromCharCode(65 + idx)}
                </span>
                <span className="text-base sm:text-lg text-brand-forest font-body">{opt}</span>
              </span>
              <ArrowRight className="w-5 h-5 flex-shrink-0 text-brand-border group-hover:text-brand-forest group-hover:translate-x-1 transition-all" />
            </motion.button>
          ))}
        </motion.div>

        {canBack && (
          <div className="mt-8 sm:mt-10">
            <button
              onClick={onBack}
              className="inline-flex items-center gap-2 text-sm text-brand-muted hover:text-brand-forest transition-colors font-body"
              data-testid="quiz-back-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Previous question
            </button>
          </div>
        )}
      </div>
    </motion.section>
  );
};
